import * as React from 'react';
import { useIntl } from 'react-intl';
import { useAppSelector as useSelector } from 'Store/index';
import { filterConfirmedSeizures, TRelevantDocumentType } from '../utils';
import { sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { ISpecificNoDateData } from 'Components/DashboardTile/TileContentMaker/components/SpecificListNoDate';
import { filterByDocumentType, sortDocumentsDescending } from 'Utility/documentHandling';

const SeizuresDash = ({ documents }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const sortedAndMergedDocs = useSelector((state: IState) => state.myapp.sortedAndMergedDocuments);
  const seizureDocs = filterByDocumentType(documents, 'seizure');
  const patientReportedSeizureDocs = filterByDocumentType(sortedAndMergedDocs, 'patientReportedSeizure');

  const unhandledPatRepSeizDocs = patientReportedSeizureDocs.filter(d => !d.status);
  const unhandledPatRepSeizAmount = unhandledPatRepSeizDocs.length;
  const latestUnhandledPatRepSeiz = unhandledPatRepSeizDocs.sort((a, b) =>
    sortPartialDate(b.startDate, a.startDate),
  )[0];

  const acceptedPatRepSeizDocs = patientReportedSeizureDocs.filter(d => d.status === 'accepted');

  const allSeizureDocsSorted = [...filterConfirmedSeizures(seizureDocs), ...acceptedPatRepSeizDocs].sort(
    sortDocumentsDescending,
  );

  // latest seizure free period
  const seizureFreePeriods = filterByDocumentType(documents, 'seizureFreePeriod').sort(sortDocumentsDescending);
  const latestPeriod = seizureFreePeriods.at(0);

  const listData: Array<ISpecificNoDateData> = [
    {
      title: fm('patientReportedSeizure.amountOfUnhandled'),
      value: unhandledPatRepSeizAmount,
      key: 'amountOfUnhandled',
    },
    {
      title: fm('seizure.seizureFreePeriod'),
      value:
        latestPeriod?.startDate || latestPeriod?.endDate ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'left' }}>
              <div>{formatPartialDate(latestPeriod.startDate)} –</div>
              <div>{formatPartialDate(latestPeriod.endDate)}</div>
            </div>
          </div>
        ) : undefined,
      key: 'latestSeizureFreePeriod',
    },
  ];

  if (unhandledPatRepSeizAmount > 0) {
    listData.unshift({
      title: fm('patientReportedSeizure.latestUnhandled'),
      value: formatPartialDate(latestUnhandledPatRepSeiz?.startDate),
      key: 'latestUnhandled',
    });
  }

  if (allSeizureDocsSorted.length > 0 && allSeizureDocsSorted[0]) {
    let date: PartialDate | undefined | null;
    if ('date' in allSeizureDocsSorted[0]) date = allSeizureDocsSorted[0].date;
    else if ('startDate' in allSeizureDocsSorted[0]) date = allSeizureDocsSorted[0].startDate ?? undefined;

    if (date) {
      listData.unshift({
        title: fm('seizure.latestAccepted'),
        value: formatPartialDate(date),
        key: 'latestAccepted',
      });
    }
  }

  return <TileContentMaker type="specificListNoDate" data={listData} />;
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default SeizuresDash;
